import { CompareType, DataSelectType } from 'webapp/enums/enums';
import { sanitize } from 'webapp/utils/html';

import type DChartComparePoint from 'webapp/d-chart/point/compare-point';
import DChartTooltipBase from 'webapp/d-chart/tooltip/base';

export default class DChartTooltipForecast extends DChartTooltipBase {
	override tooltipTypeClass = 'forecast';

	override mainPointBeforeCompares = false; // main point on bottom
	override showNoValuePoints = false; // dont show forecasts or main point if no value
	override showCompareChangeStr = false; // will show at actual value - see formatPrimaryBottom

	get forecastComparePoint() {
		return this.comparePoints.find((cp) => cp.isForecast);
	}

	protected override getTooltipTop() {
		// date on top
		return `<div class="top"><div class="title">${this.timeLabel}</div></div>`;
	}

	protected override formatPrimaryTop() {
		const title = 'Actual value'; // sanitize(this.titleLong);
		return `<div class="color-${this.colorIndexOrClass}"><div class="title">${title}</div></div>`;
	}

	protected override formatPrimaryBottom() {
		const forecastPoint = this.forecastComparePoint;
		if (forecastPoint) {
			return this.formatCompareChangeStr(forecastPoint);
		}
		return '';
	}

	protected override getPrimaryInnerTooltip() {
		// check if this is a forecast-only scenario where the primary point is actually a forecast
		// in this case, we don't want to show "Actual value" since there's no actual data
		const isForecastOnly = this.point.series?.type === DataSelectType.FORECAST;

		if (isForecastOnly) {
			return '';
		}

		return super.getPrimaryInnerTooltip();
	}

	protected override getCompareTop(comparePoint: DChartComparePoint) {
		if (comparePoint.title) {
			const colorClass = this.getColorIndexOrClassForCompare(comparePoint);
			return `<div class="color-${colorClass}"><div class="title color-fg">${sanitize(comparePoint.title)}</div></div>`;
		}
		return '';
	}

	override getTooltipHtmlForPoint(comparePoint?: DChartComparePoint): string {
		const selectedPoint = comparePoint || this;

		// TOP
		const topHtml = this.getTooltipTop();

		const comparePoints = this.comparePoints;

		// Separate RANGE type compares from others (forecast types)
		const rangeCompares = comparePoints.filter((cp) => cp.compareType === CompareType.RANGE);
		const otherCompares = comparePoints.filter((cp) => cp.compareType !== CompareType.RANGE);

		// MAIN point
		let mainPointHtml = '';
		{
			const isSelected = selectedPoint === this && comparePoints.length > 0,
				pointHtml = this.getPrimaryInnerTooltip();
			if (pointHtml) {
				mainPointHtml = this.wrapTooltipPoint(pointHtml, false, isSelected);
			}
		}

		// OTHER COMPARE points (forecasts) - these go before main point
		let otherComparesHtml = '';
		{
			otherCompares.forEach((point) => {
				const isSelected = selectedPoint === point,
					pointHtml = this.getCompareInnerTooltip(point);
				if (pointHtml) {
					otherComparesHtml += this.wrapTooltipPoint(pointHtml, true, isSelected);
				}
			});
		}

		// RANGE COMPARE points - these go after main point
		let rangeComparesHtml = '';
		{
			rangeCompares.forEach((point) => {
				const isSelected = selectedPoint === point,
					pointHtml = this.getCompareInnerTooltip(point);
				if (pointHtml) {
					rangeComparesHtml += this.wrapTooltipPoint(pointHtml, true, isSelected);
				}
			});
		}

		// other compares, then main point, then range compares
		let output = topHtml + otherComparesHtml + mainPointHtml + rangeComparesHtml;

		// only show "Click for more actions" if hovering over main point or a non-forecast compare
		const isHoveringForecastCompare =
			selectedPoint !== this && (selectedPoint as DChartComparePoint).isOneOfForecasts;
		if (!isHoveringForecastCompare) {
			output += this.getTooltipBottom();
		}

		return output;
	}
}
